import React, { useContext, useRef, useState, useEffect } from 'react';
import { Form, Input, Table, FormInstance, InputRef } from 'antd';
import { ColumnType } from 'antd/es/table';
import {
  DataTypeCustomTable,
  EditableRowProps,
  EditableCellProps,
} from 'store/ducks/Global/types';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

type FixedType = 'left' | 'right' | undefined;

export interface EditableColumnType<
  T = DataTypeCustomTable,
> extends ColumnType<T> {
  editable?: boolean;
  fixed?: FixedType;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  children?: EditableColumnType<T>[];
  type?: string;
}

type ColumnTypes = EditableColumnType[];

const EditableContext = React.createContext<FormInstance<any> | null>(null);

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext)!;

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
    if (!editing) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log(' failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    if (typeof record[dataIndex] === 'boolean') {
      childNode = editing ? (
        <Form.Item
          style={{ margin: 0 }}
          name={dataIndex}
          valuePropName="checked"
        >
          <Input
            type="checkbox"
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            onChange={save}
          />
        </Form.Item>
      ) : (
        <div className="editable-cell-value-wrap" onClick={toggleEdit}>
          {record[dataIndex] ? 'True' : 'False'}
        </div>
      );
    } else {
      childNode = editing ? (
        <Form.Item style={{ margin: 0 }} name={dataIndex}>
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            onChange={async (e) => {
              form.setFieldsValue({ [dataIndex]: e.target.value });
              handleSave({ ...record, [dataIndex]: e.target.value });
            }}
          />
        </Form.Item>
      ) : (
        <div className="editable-cell-value-wrap" onClick={toggleEdit}>
          {children}
        </div>
      );
    }
  }

  return <td {...restProps}>{childNode}</td>;
};

const CustomTable = <T extends DataTypeCustomTable>({
  columns,
  dataSource,
  locale,
  handleSave,
}: {
  columns: ColumnTypes;
  dataSource: T[];
  handleSave: (row: T) => void;
  locale?: {
    emptyText: React.ReactNode;
  };
  pagination?: {
    current: number;
    pageSize: number;
    onChange: (page: number, pageSize: number) => void;
    showSizeChanger: boolean;
  };
}) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '100'],
    position: ['topRight'] as ['topRight'],
  });

  const onPaginationChange = (page: number, pageSize: number) => {
    setPagination({ ...pagination, current: page, pageSize });
  };

  const topScrollRef = useRef<HTMLDivElement>(null);
  const topScrollInnerRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const findScrollable = () => {
      const el = document.querySelector(
        '.ant-table-body, .ant-table-content',
      ) as HTMLDivElement | null;

      if (el) {
        tableContentRef.current = el;
      }
    };

    findScrollable();
    const observer = new MutationObserver(findScrollable);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const top = topScrollRef.current;
    const table = tableContentRef.current;

    if (!top || !table) return;

    const syncTop = () => {
      if (top) top.scrollLeft = table.scrollLeft;
    };
    const syncBottom = () => {
      if (table) table.scrollLeft = top.scrollLeft;
    };

    top.addEventListener('scroll', syncBottom);
    table.addEventListener('scroll', syncTop);

    return () => {
      top.removeEventListener('scroll', syncBottom);
      table.removeEventListener('scroll', syncTop);
    };
  }, [dataSource, columns]);

  useEffect(() => {
    const table = tableContentRef.current;
    if (table && topScrollInnerRef.current) {
      topScrollInnerRef.current.style.width = `${table.scrollWidth}px`;
    }
  }, [dataSource, columns]);

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const processedColumns = columns.map((col) =>
    col.editable
      ? {
          ...col,
          onCell: (record: T) => ({
            record,
            editable: col.editable,
            dataIndex: col.dataIndex,
            title: col.title,
            handleSave,
          }),
        }
      : col,
  );

  return (
    <Styled.TypeComponent>
      <Styled.TopScrollBar ref={topScrollRef}>
        <div ref={topScrollInnerRef} />
      </Styled.TopScrollBar>

      <Table
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={dataSource}
        columns={processedColumns as ColumnTypes}
        pagination={{
          ...pagination,
          onChange: onPaginationChange,
        }}
        scroll={{ x: 'max-content' }}
        locale={locale}
      />
    </Styled.TypeComponent>
  );
};

export default CustomTable;
